/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / DISCOUNTS — Pure-Engine prozentualer Rabatte (S179, Teil B · R1)
   =============================================================================
   Reine, DOM-freie Logik der Rabatt-Anwendung (Vitest-getestet). Mindert das
   Brutto JE STEUERSATZ anteilig und leitet daraus GoBD-konsistente Pro-Satz-
   Netto/Steuer-Blöcke ab. R3 klinkt sich beim Kassieren ein (vor TSE-Signatur).

   GoBD-RUNDUNGSREGEL (kritisch, Memory gg-receipt-net-per-rate):
   Pro-Satz-Netto wird aus dem GERABATTETEN Brutto-pro-Satz MINUS Steuer
   gerechnet — NICHT aus round(tax/satz) einer Summe (letzteres verschiebt die
   Bemessungsgrundlage je ~3 ct, S143c-Befund). Modell exakt wie
   computeOrderReceipt in src/views/admin/receipt.js (_gross7/_gross19):
   net_satz = brutto_satz − steuer_satz. So gilt je Satz net+tax == brutto und
   Σ Pro-Satz-Brutto == grossAfter.

   RABATT JE SATZ: erst das Brutto je Satz rabattieren + auf Cent runden, DANN
   Steuer/Netto aus dem gerabatteten Brutto-pro-Satz ableiten (nicht den
   Gesamt-Brutto rabattieren und danach splitten).

   PURITY: keine Date.now()/Math.random(), keine I/O. Alle Beträge in Cent (int).
============================================================================ */

/**
 * Validiert + normalisiert einen Prozentsatz (Core defensiv; DB hat zusätzlich
 * einen CHECK 0<percent<=100). Wirft bei ungültigem Wert.
 * @param {number} percent
 * @returns {number}
 */
export function assertValidPercent(percent) {
  const p = Number(percent);
  if (!Number.isFinite(p) || p <= 0 || p > 100) {
    throw new RangeError(`Ungültiger Rabatt-Prozentsatz: ${percent} (erlaubt: 0 < x ≤ 100).`);
  }
  return p;
}

// Brutto je Satz auf Cent runden (round half up; Beträge sind ≥ 0 → JS-Math.round ok).
function _roundCents(v) {
  return Math.round(v);
}

/**
 * Wendet einen prozentualen Rabatt JE STEUERSATZ auf ein Brutto-Paar an und
 * liefert den vollständigen, GoBD-konsistenten Steuerblock NACH Rabatt.
 *
 * @param {{gross7?: number, gross19?: number}} grossByRate  Brutto-Cent je Satz
 *        (z. B. aus computeOrderReceipt: _gross7 / _gross19).
 * @param {number} percent  Prozentsatz 0<x<=100.
 * @returns {{
 *   percent: number,
 *   grossBefore: number,
 *   gross7After: number, gross19After: number,
 *   net7After: number, net19After: number,
 *   tax7After: number, tax19After: number,
 *   netAfter: number, taxAfter: number,
 *   grossAfter: number,
 *   discountCents: number
 * }}
 */
export function applyDiscountPerRate(grossByRate, percent) {
  const p = assertValidPercent(percent);

  const src = grossByRate || {};
  const gross7  = Math.max(0, Math.trunc(Number(src.gross7)  || 0));
  const gross19 = Math.max(0, Math.trunc(Number(src.gross19) || 0));

  const factor = (100 - p) / 100;

  // 1) Brutto JE SATZ rabattieren + auf Cent runden.
  const gross7After  = _roundCents(gross7  * factor);
  const gross19After = _roundCents(gross19 * factor);

  // 2) Steuer aus dem gerabatteten Brutto-pro-Satz herausrechnen, Netto = Brutto − Steuer.
  //    tax_satz = round(brutto_satz * satz / (100 + satz)); garantiert net+tax==brutto je Satz.
  const tax7After  = _roundCents(gross7After  * 7  / 107);
  const tax19After = _roundCents(gross19After * 19 / 119);
  const net7After  = gross7After  - tax7After;
  const net19After = gross19After - tax19After;

  const grossBefore = gross7 + gross19;
  const grossAfter  = gross7After + gross19After;

  return {
    percent: p,
    grossBefore,
    gross7After, gross19After,
    net7After, net19After,
    tax7After, tax19After,
    netAfter: net7After + net19After,
    taxAfter: tax7After + tax19After,
    grossAfter,
    discountCents: grossBefore - grossAfter
  };
}

/**
 * Wie applyDiscountPerRate, aber mit DEAL-AUSSCHLUSS (S179 R3, Jonas-Direktive
 * „vom Rabatt ausgeschlossen sind z. B. Deals"). Der Deal-Anteil je Satz läuft
 * VOLL durch; nur der Nicht-Deal-Anteil wird gemindert. Steuer/Netto werden je
 * Satz aus dem KOMBINIERTEN Brutto (Deal voll + Nicht-Deal gerabattet) heraus-
 * gerechnet — eine Rundung pro Satz auf den tatsächlich gezahlten Betrag.
 *
 * Dieses Modell wird in mark_order_paid (phase_132) server-seitig 1:1 gespiegelt
 * (SQL ist autoritativ für die gespeicherten/signierten Totals; diese Funktion
 * dient Live-Vorschau + Paritäts-Test). Bei dealGross7/19 = 0 identisch zu
 * applyDiscountPerRate.
 *
 * @param {{dealGross7?: number, dealGross19?: number, discountableGross7?: number, discountableGross19?: number}} grossSplit
 * @param {number} percent  Prozentsatz 0<x<=100.
 * @returns analog applyDiscountPerRate, zusätzlich discountableBeforeCents.
 */
export function applyDiscountExcludingDeals(grossSplit, percent) {
  const p = assertValidPercent(percent);

  const src = grossSplit || {};
  const d7  = Math.max(0, Math.trunc(Number(src.dealGross7)  || 0));
  const d19 = Math.max(0, Math.trunc(Number(src.dealGross19) || 0));
  const n7  = Math.max(0, Math.trunc(Number(src.discountableGross7)  || 0));
  const n19 = Math.max(0, Math.trunc(Number(src.discountableGross19) || 0));

  const factor = (100 - p) / 100;

  // Nur den Nicht-Deal-Anteil je Satz mindern + auf Cent runden.
  const n7After  = _roundCents(n7  * factor);
  const n19After = _roundCents(n19 * factor);

  // Kombiniertes Brutto je Satz (Deal voll + Nicht-Deal gerabattet).
  const gross7After  = d7  + n7After;
  const gross19After = d19 + n19After;

  // Steuer aus dem kombinierten Brutto-pro-Satz herausrechnen.
  const tax7After  = _roundCents(gross7After  * 7  / 107);
  const tax19After = _roundCents(gross19After * 19 / 119);
  const net7After  = gross7After  - tax7After;
  const net19After = gross19After - tax19After;

  const grossBefore = d7 + d19 + n7 + n19;
  const grossAfter  = gross7After + gross19After;

  return {
    percent: p,
    grossBefore,
    discountableBeforeCents: n7 + n19,
    gross7After, gross19After,
    net7After, net19After,
    tax7After, tax19After,
    netAfter: net7After + net19After,
    taxAfter: tax7After + tax19After,
    grossAfter,
    discountCents: grossBefore - grossAfter
  };
}
